<template>
    <button
        ref="kebabBtn"
        class="kebab-btn"
        aria-haspopup="menu"
        :aria-expanded="isOpen ? 'true' : 'false'"
        :aria-label="ariaLabel"
        @click="toggleMenu"
    >
        <span class="kebab-dots" aria-hidden="true"></span>
    </button>

    <Teleport to="body">
        <Transition :name="transitionName" :css="useTransition">
            <div
                v-if="isOpen"
                ref="menu"
                class="menu-popover"
                :class="{ 'pointer-all': pointerAll }"
                :style="menuStyle"
                role="menu"
                @keydown.esc.stop.prevent="closeMenu"
            >
                <slot :close="closeMenu" />
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";

const props = withDefaults(
    defineProps<{
        openId?: number | string | null;
        itemId?: number | string;
        ariaLabel?: string;
        pointerAll?: boolean;
        useTransition?: boolean;
        transitionName?: string;
    }>(),
    {
        ariaLabel: "Open actions",
        pointerAll: false,
        useTransition: true,
        transitionName: "fade",
    },
);

const emit = defineEmits<{
    (e: "update:openId", value: number | string | null): void;
}>();

const internalOpen = ref(false);

const isControlled = computed(
    () => props.openId !== undefined && props.itemId !== undefined,
);

const isOpen = computed(() =>
    isControlled.value ? props.openId === props.itemId : internalOpen.value,
);

const kebabBtn = ref<HTMLElement | null>(null);
const menu = ref<HTMLElement | null>(null);

const menuPos = ref({ top: 0, left: 0 });

const menuStyle = computed(() => ({
    position: "fixed",
    top: `${menuPos.value.top}px`,
    left: `${menuPos.value.left}px`,
    zIndex: "99999",
}));

const setOpen = (value: boolean) => {
    if (isControlled.value) {
        emit("update:openId", value ? (props.itemId ?? null) : null);
        return;
    }

    internalOpen.value = value;
};

const updateMenuPosition = () => {
    const btn = kebabBtn.value;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const padding = 8;

    const menuW = menu.value?.offsetWidth ?? 176;
    const menuH = menu.value?.offsetHeight ?? 0;

    let top = rect.bottom + 8;
    let left = rect.right - menuW;

    left = Math.min(
        Math.max(left, padding),
        window.innerWidth - menuW - padding,
    );

    if (menuH && top + menuH > window.innerHeight - padding) {
        top = Math.max(padding, rect.top - menuH - 8);
    }

    menuPos.value = { top, left };
};

const toggleMenu = () => {
    setOpen(!isOpen.value);
};

const closeMenu = () => {
    setOpen(false);
};

const onClickOutside = (e: MouseEvent) => {
    if (!isOpen.value) return;
    const target = e.target as Node;
    if (
        menu.value &&
        !menu.value.contains(target) &&
        kebabBtn.value &&
        !kebabBtn.value.contains(target)
    ) {
        closeMenu();
    }
};

const onScrollOrResize = () => {
    if (!isOpen.value) return;
    updateMenuPosition();
};

const addListeners = () => {
    document.addEventListener("click", onClickOutside);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
};

const removeListeners = () => {
    document.removeEventListener("click", onClickOutside);
    window.removeEventListener("resize", onScrollOrResize);
    window.removeEventListener("scroll", onScrollOrResize, true);
};

watch(isOpen, async (open) => {
    if (open) {
        await nextTick();
        requestAnimationFrame(() => updateMenuPosition());
        addListeners();
        return;
    }

    removeListeners();
});

onBeforeUnmount(() => {
    removeListeners();
});
</script>

<style scoped>
.kebab-btn {
    display: inline-flex;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    border-radius: 8px;
}
.kebab-btn:hover {
    background: #f3f4f6;
}

.kebab-dots {
    width: 4px;
    height: 4px;
    border-radius: 999px;
    background: #6b7280;
    position: relative;
}
.kebab-dots::before,
.kebab-dots::after {
    content: "";
    position: absolute;
    left: 0;
    width: 4px;
    height: 4px;
    border-radius: 999px;
    background: #6b7280;
}
.kebab-dots::before {
    top: -7px;
}
.kebab-dots::after {
    top: 7px;
}

.menu-popover {
    z-index: 99999;
    width: 176px;
    border: 1px solid #e5e7eb;
    background: #fff;
    border-radius: 8px;
    box-shadow:
        0 8px 24px rgba(16, 24, 40, 0.12),
        0 2px 8px rgba(16, 24, 40, 0.06);
    padding: 6px 0;
}

.menu-popover.pointer-all:hover,
.menu-popover.pointer-all :deep(.menu-item:hover) {
    cursor: pointer;
}

:deep(.menu-item) {
    display: block;
    width: 100%;
    text-align: left;
    padding: 8px 12px;
    font-size: 14px;
    line-height: 20px;
    color: #374151;
}
:deep(.menu-item:hover) {
    background: #f9fafb;
}

:deep(.menu-item.danger) {
    color: #b91c1c;
}

:deep(.menu-item.warning) {
    color: #a68a00;
}
:deep(.menu-item.danger:hover) {
    background: #fef2f2;
    cursor: pointer;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.12s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
